import { InvalidPlaidAccount } from "@common/dto/plaid/plaid-common";
import { pluralize } from "@common/helpers/string";
import { PlaidConnectionWithAccounts } from "@common/types/plaidConnection";
import React, { useCallback, useState } from "react";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useFinancialAccountEntitySelection } from "@hooks/useFinancialAccountEntitySelection";
import { useUser } from "@hooks/useUser";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { HStack } from "@design-system/HStack/HStack";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { AccountsConnectionProvider } from "../../financialAccount/AccountConnectionWidget/AccountsConnectionProvider";
import { isValidConnection } from "../../financialAccount/AccountConnectionWidget/helpers";
import "./PlaidAccountsConnectedModal.scss";
import { PlaidAccountsConnectedModalBody } from "./PlaidAccountsConnectedModalBody";

interface PlaidAccountsConnectedModalProps {
    show: boolean;
    connection: PlaidConnectionWithAccounts;
    foreign: InvalidPlaidAccount[] | null;
    connectionSaved: boolean;
    /** Hides the per-account Personal/Business classification step. */
    hideAccountTypeSelection?: boolean;
    onHide(): void;
}

export const PlaidAccountsConnectedModal: React.FC<
    PlaidAccountsConnectedModalProps
> = ({
    show,
    connection,
    foreign,
    connectionSaved,
    hideAccountTypeSelection = false,
    onHide,
}) => {
    const { showPersonalEntitySelection, showBusinessEntitySelection } =
        useFinancialAccountEntitySelection();
    const showEntitySelection =
        showPersonalEntitySelection || showBusinessEntitySelection;
    const [submitted, setSubmitted] = useState<boolean>(false);
    const user = useUser();
    const isWithEntities = Boolean(
        connection &&
        showEntitySelection &&
        connectionSaved &&
        !hideAccountTypeSelection,
    );

    const handleSubmit = useCallback(() => {
        if (
            hideAccountTypeSelection ||
            (connection && isValidConnection(connection)) ||
            submitted ||
            !user?.onboardingComplete
        ) {
            setSubmitted(false);
            onHide();
        } else {
            setSubmitted(true);
        }
    }, [
        connection,
        onHide,
        submitted,
        user?.onboardingComplete,
        hideAccountTypeSelection,
    ]);

    const getTitle = (): React.ReactNode => {
        if (!connectionSaved) {
            return <>Not able to connect selected accounts</>;
        }
        if (hideAccountTypeSelection) {
            return <>Some accounts could not be connected</>;
        }
        return (
            <>
                Confirm whether your{" "}
                {pluralize("account", connection.accounts.length)}{" "}
                {connection.accounts.length > 1 ? "are" : "is"} Personal or
                Business
            </>
        );
    };
    const title = getTitle();

    useKeyboardCommand({
        keys: "enter",
        callback: handleSubmit,
        options: {
            description: "Continue",
            enabled: show,
        },
    });

    return (
        <AccountsConnectionProvider
            showAccountTypeWarning={Boolean(
                submitted && user?.onboardingComplete,
            )}
        >
            <PlainModal
                show={show}
                onHide={onHide}
                size="lg"
                backdrop={connection ? true : "static"}
                dialogClassName={
                    isWithEntities
                        ? "accounts-connected-modal__dialog--with-entities"
                        : "accounts-connected-modal__dialog"
                }
            >
                <>
                    <StandardModalHeader>{title}</StandardModalHeader>
                    <PlaidAccountsConnectedModalBody
                        connection={connection}
                        connectionSaved={connectionSaved}
                        foreign={foreign}
                        hideAccountTypeSelection={hideAccountTypeSelection}
                    />

                    <HStack as="footer" align="center" justify="end">
                        <WithHotkey
                            hotKey="enter"
                            hotKeyOptions={{ description: "Continue" }}
                            hotKeyDisplayPosition="inline"
                        >
                            {(hotkeyHint) => (
                                <Button
                                    onClick={handleSubmit}
                                    size="xl"
                                    variant="primary"
                                    rightIcon={hotkeyHint}
                                >
                                    Continue
                                </Button>
                            )}
                        </WithHotkey>
                    </HStack>
                </>
            </PlainModal>
        </AccountsConnectionProvider>
    );
};
